/**
 * Why an invoice action was refused, for the codes the server's invoice and e-invoice guards throw.
 *
 * Each screen maps the codes it expects; the app-wide error handlers fall back on this, so a screen
 * that meets a code it did not expect (a claim finalized in another tab, a release on a reported
 * invoice) still says why instead of "Something went wrong".
 */
export const INVOICE_ERROR_KEYS = {
  PATIENT_EINVOICE_SUBMITTED: 'invoice.eInvoiceSubmitted',
  INVOICE_REPORTED_TO_ZATCA: 'invoice.eInvoiceSubmitted',
  INSURANCE_EINVOICE_SUBMITTED: 'invoice.reReport.legacyInsuranceLocked',
  INVOICE_CLAIM_LOCKED: 'invoice.claimLocked',
  INVOICE_CANCELLED: 'invoice.cancel.cancelledLocked',
  EINVOICE_REPORT_IN_FLIGHT: 'invoice.reReport.inFlight',
  MSG_INVOICE_EXCESS_NOT_COVERED_BY_PATIENT_PAYMENTS: 'invoice.excessPayment.notCovered',
  MSG_INVOICE_LAST_LINE_REMOVAL: 'invoice.lastLineRemoval'
}

const CODE = /^[A-Z][A-Z0-9_]+$/

/**
 * The translated reason for an error's code, or null when it has none. Codes of the e-invoice
 * reports (MSG_…, INVOICE_…) are looked up under `invoice.errors`, as the invoice screen does.
 *
 * @param {{ message?: string } | undefined} error
 * @param {(key: string, options?: object) => string} t
 * @returns {string | null}
 */
export const invoiceErrorMessage = (error, t) => {
  const code = (error?.message || '').replace(/^GraphQL error: /, '').trim()
  if (!CODE.test(code)) return null
  if (INVOICE_ERROR_KEYS[code]) return t(INVOICE_ERROR_KEYS[code])
  return t(`invoice.errors.${code.toLowerCase()}`, { defaultValue: '' }) || null
}

const MESSAGE_CODE = /^MSG_[A-Z0-9_]+$/

/**
 * A message kept on an e-invoice submission, in the user's language. Dentolize's own messages are
 * kept as their code (MSG_…), alone or as JSON `{ code, params, message }` when they have values;
 * a code is looked up under `invoice.eInvoice.messages`, then `invoice.errors`. ZATCA's messages,
 * and codes with no translation, are shown as they were kept.
 *
 * @param {string} raw
 * @param {(key: string, options?: object) => string} t
 * @returns {string}
 */
export const eInvoiceSubmissionMessage = (raw, t) => {
  const text = String(raw || '').trim()
  let code = text
  let params = {}
  let fallback = text
  if (text.startsWith('{') && text.endsWith('}')) {
    try {
      const parsed = JSON.parse(text)
      code = parsed.code || ''
      params = parsed.params || {}
      fallback = parsed.message || text
    } catch {
      // Not JSON after all: shown as kept
    }
  }
  if (!MESSAGE_CODE.test(code)) return fallback
  const options = { ...params, defaultValue: '', interpolation: { escapeValue: false } }
  return t(`invoice.eInvoice.messages.${code}`, options) || t(`invoice.errors.${code.toLowerCase()}`, options) || fallback
}
